export const aboutPageContent = {
  meta: {
    title: "About TheCareerSpace | Admissions Guidance for Students & Families",
    description:
      "Learn how TheCareerSpace supports students and parents with clear, structured admissions guidance across bachelor's, master's, and MBBS pathways."
  },
  hero: {
    badge: "About TheCareerSpace",
    title: "A counselling-driven admissions partner for students and families.",
    description:
      "TheCareerSpace was built to simplify admission decisions through practical guidance, transparent conversations, and structured support across key education pathways.",
    highlights: [
      "Profile-led recommendations",
      "Parent-friendly decision support",
      "Step-by-step guidance till closure"
    ]
  },
  whoWeHelp: [
    {
      title: "Students",
      description:
        "Students who need clear direction on pathways, college options, and next steps."
    },
    {
      title: "Parents",
      description:
        "Parents who need confidence, clarity, and practical understanding before key admission decisions."
    },
    {
      title: "Families",
      description:
        "Families navigating complex options and timelines who want structured, calm guidance."
    }
  ],
  philosophy:
    "Our approach is simple: understand context first, recommend responsibly, and guide consistently. Admissions are not just about options; they are about fit, clarity, and informed choices.",
  reliabilityPoints: [
    "Recommendations grounded in profile and feasibility",
    "Clear communication instead of vague counselling",
    "Guidance that continues through major milestones",
    "Balanced conversations with student and parent alignment"
  ],
  studentParentView: {
    student:
      "Students receive practical direction so they can move from confusion to clear action.",
    parent:
      "Parents receive transparent conversations to support responsible, informed decisions."
  }
};

export const servicesPageContent = {
  meta: {
    title: "Admissions Services | TheCareerSpace",
    description:
      "Explore TheCareerSpace admissions services across bachelor's, master's, MBBS in India, and MBBS abroad with structured counselling and process support."
  },
  hero: {
    badge: "Admissions Services",
    title: "Guidance built around major admission pathways and real family decisions.",
    description:
      "From early exploration to final closure, TheCareerSpace supports students and parents with a structured counselling model across four key pathways.",
    highlights: [
      "Four focused pathways",
      "Profile-based service design",
      "End-to-end guidance model"
    ]
  }
};

export const admissionsPagesContent = {
  bachelorsIndia: {
    meta: {
      title: "Bachelor's Admissions in India | TheCareerSpace",
      description:
        "Guidance for undergraduate admissions in India with practical shortlisting, family clarity, and process support."
    },
    hero: {
      badge: "Bachelor's Admissions in India",
      title: "Confident undergraduate admission decisions after Class 12.",
      description:
        "For students and families evaluating bachelor pathways across India with clear support for options, fit, and next steps.",
      highlights: [
        "Undergraduate pathway clarity",
        "Shortlist with fit and feasibility",
        "Application guidance support"
      ]
    },
    intro:
      "The transition after Class 12 often brings multiple choices and uncertainty. This journey focuses on helping students and parents compare options clearly and move forward with confidence.",
    forWho: [
      "Students uncertain about course direction",
      "Families comparing colleges across states",
      "Students who need practical shortlist support"
    ],
    decisionPoints: [
      "Choosing between multiple course directions",
      "Balancing aspiration with practical feasibility",
      "Understanding what to prioritize in college selection",
      "Managing applications and documentation timelines"
    ],
    supportBlocks: [
      {
        title: "Direction conversations",
        description:
          "Discuss student interests, strengths, and practical constraints before finalizing path choices."
      },
      {
        title: "Structured shortlist building",
        description:
          "Create a balanced shortlist with a clear rationale for each option."
      },
      {
        title: "Guided admission flow",
        description:
          "Track documents and application milestones in a sequence families can follow."
      }
    ],
    collegePreview: [
      "Sandip University",
      "Parul University",
      "GD Goenka",
      "Marwadi",
      "Presidency",
      "SRM"
    ]
  },
  mastersIndia: {
    meta: {
      title: "Master's Admissions in India | TheCareerSpace",
      description:
        "Postgraduate admissions guidance in India with profile-led planning, course-fit support, and structured admission flow."
    },
    hero: {
      badge: "Master's Admissions in India",
      title: "Postgraduate planning with clearer strategy and stronger fit.",
      description:
        "For graduates evaluating master's pathways in India with focused support for course selection, college fit, and admissions process planning.",
      highlights: [
        "Career-aligned academic planning",
        "Focused profile-based shortlisting",
        "Structured process support"
      ]
    },
    intro:
      "Master's decisions often carry both academic and career implications. This journey helps graduates and families evaluate options with structured guidance and clear priorities.",
    forWho: [
      "Graduates exploring specialization choices",
      "Students planning progression-focused admissions",
      "Families seeking practical course and college clarity"
    ],
    decisionPoints: [
      "Selecting suitable specialization direction",
      "Evaluating college options against profile strength",
      "Planning admissions around timelines",
      "Aligning academic goals with long-term progression"
    ],
    supportBlocks: [
      {
        title: "Specialization fit support",
        description:
          "Understand which master's direction aligns best with student goals and context."
      },
      {
        title: "Strategic shortlist planning",
        description:
          "Build a list that balances quality, feasibility, and progression potential."
      },
      {
        title: "Application milestone tracking",
        description:
          "Keep submissions, documents, and follow-up steps organized end to end."
      }
    ],
    collegePreview: [
      "VIT",
      "SRM",
      "Amrita",
      "IILM",
      "Woxsen University",
      "ICFAI"
    ]
  },
  mbbsIndia: {
    meta: {
      title: "MBBS in India Guidance | TheCareerSpace",
      description:
        "Practical and parent-trust-oriented MBBS in India guidance with structured counselling and clear admission decision support."
    },
    hero: {
      badge: "MBBS in India",
      title: "Careful guidance for one of the most important admission decisions.",
      description:
        "For students and families planning MBBS in India with high-trust conversations, balanced option clarity, and structured support.",
      highlights: [
        "Parent-trust-oriented counselling",
        "Decision clarity for medical pathways",
        "Structured guidance conversations"
      ]
    },
    intro:
      "MBBS decisions in India are often high-stakes for families. This journey is designed to keep discussions informed, calm, and practically oriented.",
    forWho: [
      "Students focused on MBBS in India",
      "Parents seeking clear and responsible guidance",
      "Families wanting structured decision support"
    ],
    decisionPoints: [
      "Evaluating available pathway directions",
      "Balancing student ambition with practical constraints",
      "Reducing confusion around next-step priorities",
      "Maintaining decision clarity under pressure"
    ],
    supportBlocks: [
      {
        title: "Family-centred counselling",
        description:
          "Keep both student and parent aligned through practical, transparent discussions."
      },
      {
        title: "Option clarity support",
        description:
          "Compare options with a structured approach to reduce decision fatigue."
      },
      {
        title: "Process continuity",
        description:
          "Receive steady guidance through major stages of the admission journey."
      }
    ],
    collegePreview: [
      "NIMS",
      "Bharath",
      "Dr. MGR",
      "Dhanalakshmi Srinivas",
      "Hindustan Chennai",
      "SAI University"
    ]
  },
  mbbsAbroad: {
    meta: {
      title: "MBBS Abroad Guidance | TheCareerSpace",
      description:
        "Reassuring MBBS abroad counselling for students and parents with practical evaluation, planning clarity, and decision support."
    },
    hero: {
      badge: "MBBS Abroad",
      title: "International MBBS planning with calm, practical family guidance.",
      description:
        "For families exploring MBBS abroad and seeking clarity on suitability, planning discipline, and informed decision-making.",
      highlights: [
        "Country and college-fit evaluation",
        "Parent-inclusive counselling support",
        "Structured journey guidance"
      ]
    },
    intro:
      "Families considering MBBS abroad often need careful comparisons and confidence before committing. This journey focuses on practical clarity and decision readiness.",
    forWho: [
      "Students comparing MBBS abroad directions",
      "Parents who need transparent evaluation discussions",
      "Families seeking structured planning before commitments"
    ],
    decisionPoints: [
      "Understanding why MBBS abroad is being considered",
      "Comparing options through a practical lens",
      "Aligning expectations between student and parent",
      "Planning each step with clarity and discipline"
    ],
    supportBlocks: [
      {
        title: "Evaluation-first counselling",
        description:
          "Assess options through suitability and family comfort before moving ahead."
      },
      {
        title: "Decision reassurance",
        description:
          "Structured discussions reduce uncertainty and support confident choices."
      },
      {
        title: "Journey guidance",
        description:
          "Support continues through the major stages of the admission process."
      }
    ],
    collegePreview: [
      "Woxsen University",
      "Malla Reddy University",
      "ICFAI",
      "Amity",
      "Kaveri",
      "Vishwa Vishwani"
    ]
  }
};
